import { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import ItemGroupsActions from "../../../store/actions/items/categories/itemGroups/itemGroups.actions";
import { useNavigate } from "react-router-dom";
import { SkeletonTable } from "../../../components/general/skeleton";
import useDelayedFlag from "../../../hooks/useDelayedFlag";

export const CategoriesListPageComponent = ({ categoryActions }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const showSkeleton = useDelayedFlag(isLoading);

  // Fetch categories when the page loads or search/pagination changes
  useEffect(() => {
    fetchCategories();
  }, [pagination.pageNumber, pagination.pageSize, searchTerm]);

  const fetchCategories = async () => {
    setIsLoading(true);
    setError(null);

    const queryParams = {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
      searchTerm,
    };

    try {
      const response = await categoryActions.getCategories(queryParams);
      setCategories(response.payload.items);
      setPagination((prev) => ({
        ...prev,
        totalItems: response.payload.totalCount,
        totalPages: response.payload.totalPages,
      }));
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPagination((prev) => ({ ...prev, pageNumber: 1 })); // Reset to page 1 when searching
  };

  const handlePageChange = (newPageNumber) => {
    setPagination((prev) => ({ ...prev, pageNumber: newPageNumber }));
  };

  const handlePageSizeChange = (event) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: parseInt(event.target.value, 10),
      pageNumber: 1, // Reset to page 1 when page size changes
    }));
  };

  const editCategoryClicked = (categoryId) => {
    navigate(`/categories/edit/${categoryId}`);
  };

  return (
    <div>
      <h2>Categories List</h2>

      <div>
        <input type="text" placeholder="Search categories..." value={searchTerm} onChange={handleSearchChange} />
      </div>

      <div>
        <label>Page Size:</label>
        <select value={pagination.pageSize} onChange={handlePageSizeChange}>
          <option value="1">1</option>
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
        </select>
      </div>

      {isLoading ? (
        showSkeleton && <SkeletonTable rows={10} cols={3} />
      ) : error ? (
        <p>Error: {error.message || "Something went wrong"}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Category Name</th>
              <th>Description</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => (
              <tr key={category.id}>
                <td>{category.categoryName}</td>
                <td>{category.description}</td>
                <td>
                  <button
                    onClick={() => {
                      editCategoryClicked(category.id);
                    }}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div>
        <button disabled={pagination.pageNumber === 1} onClick={() => handlePageChange(pagination.pageNumber - 1)}>
          Previous
        </button>
        <span>
          Page {pagination.pageNumber} of {pagination.totalPages}
        </span>
        <button disabled={pagination.pageNumber === pagination.totalPages} onClick={() => handlePageChange(pagination.pageNumber + 1)}>
          Next
        </button>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    categoryActions: bindActionCreators(ItemGroupsActions, dispatch),
  };
};

export default connect(null, mapDispatchToProps)(CategoriesListPageComponent);
